import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type ChangeEvent, type SubmitEvent, useEffect, useMemo, useRef, useState } from 'react'

import type { Image } from '@/bindings/Image'
import type { Language } from '@/bindings/Language'
import type { Menu } from '@/bindings/Menu'
import type { MenuCategoriesResponse } from '@/bindings/MenuCategoriesResponse'
import type { MenuCategory } from '@/bindings/MenuCategory'
import type { MenuItem } from '@/bindings/MenuItem'
import type { QuantityStep } from '@/bindings/QuantityStep'
import type { ReorderMenuCategoriesRequest } from '@/bindings/ReorderMenuCategoriesRequest'
import type { SaveMenuCategoryRequest } from '@/bindings/SaveMenuCategoryRequest'
import type { SaveMenuItemRequest } from '@/bindings/SaveMenuItemRequest'
import { Button } from '@/components/button'
import { ConfirmDeleteButton } from '@/components/confirm-delete'
import { Field, Input, Select } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { backOfficeKeys, imagesPath, menuPath, menuQuery } from '@/features/back-office/api'
import { LocalizedNameFields } from '@/features/back-office/localized-name-fields'
import { groupByCategory, priceStep } from '@/features/back-office/menu-format'
import { useManagedRestaurant } from '@/features/back-office/scope'
import { STEPS, STEP_LABELS, formatPrice } from '@/lib/menu-format'
import { type NameDraft, namesFrom, toLocalizedText } from '@/features/back-office/name-draft'
import { claim, request, upload } from '@/lib/api'
import { maxPriceDecimals } from '@/lib/currencies'
import { useLanguage } from '@/lib/i18n/store'
import { useLocalizedText } from '@/lib/i18n/localized-text'
import type { MessageKey } from '@/lib/i18n/messages/en'
import { type Translate, useT } from '@/lib/i18n/translate'
import { MAX_IMAGE_MB, prepareImageUpload } from '@/lib/image'
import { canManageRestaurant } from '@/lib/roles'


/** What every form beneath needs from the scope: the language columns and the price rule. */
interface MenuContext {
  restaurantId: string
  main: Language
  languages: readonly Language[]
  decimals: number
}

/**
 * Back-office menu page (PRD, F3): categories in display order with reorder, and item CRUD
 * grouped under each category. Manager and above (F2 gate).
 */
export function RestaurantMenuPage() {
  const t = useT()
  const { restaurantId, context } = useManagedRestaurant()
  const allowed = canManageRestaurant(context.role)
  const menu = useQuery({ ...menuQuery(restaurantId), enabled: allowed })

  const { settings } = context
  const header = (
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="text-xl font-semibold">{t('menu.title')}</h1>
    </div>
  )
  if (!allowed) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        {header}
        <p role="alert" className="text-muted text-sm">
          {t('menu.managerOnly')}
        </p>
      </Page>
    )
  }

  return (
    <Page width="md" className="flex flex-col gap-4">
      {header}
      {menu.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {menu.error && (
        <div className="flex flex-col gap-2">
          <FormError error={menu.error} />
          <Button variant="secondary" onClick={() => void menu.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}
      {menu.data && (
        <MenuEditor
          menu={menu.data}
          context={{
            restaurantId,
            main: settings.main_language,
            languages: [settings.main_language, ...settings.secondary_languages],
            decimals: maxPriceDecimals(menu.data.currencies),
          }}
        />
      )}
    </Page>
  )
}

/** Patches the one cached menu from a mutation's response; nothing refetches on its own. */
function useMenuPatch(restaurantId: string) {
  const queryClient = useQueryClient()
  return (change: (menu: Menu) => Menu) => {
    queryClient.setQueryData(backOfficeKeys.menu(restaurantId), (menu: Menu | undefined) =>
      menu ? change(menu) : menu,
    )
  }
}

function MenuEditor({ menu, context }: { menu: Menu; context: MenuContext }) {
  const itemsByCategory = useMemo(() => groupByCategory(menu), [menu])

  return (
    <>
      <CategoriesCard menu={menu} context={context} counts={itemsByCategory} />
      {menu.categories.map((category) => (
        <ItemsCard
          key={category.id}
          menu={menu}
          context={context}
          category={category}
          items={itemsByCategory.get(category.id) ?? []}
        />
      ))}
    </>
  )
}

// ---- Categories ----

type CardStatus = 'saved' | 'deleted' | undefined

/** A card's action row: the status, then the add button where the form opens and the new row lands. */
function CardActions({
  status,
  savedKey,
  deletedKey,
  addLabel,
  addAriaLabel,
  onAdd,
}: {
  status: CardStatus
  savedKey: MessageKey
  deletedKey: MessageKey
  addLabel: string
  addAriaLabel?: string
  /** Absent while a form is open, which hides the button. */
  onAdd: (() => void) | undefined
}) {
  const t = useT()
  return (
    <>
      {status && (
        <p role="status" className="text-muted text-sm">
          {t(status === 'saved' ? savedKey : deletedKey)}
        </p>
      )}
      {onAdd && (
        <Button size="sm" variant="secondary" className="self-start" aria-label={addAriaLabel} onClick={onAdd}>
          {addLabel}
        </Button>
      )}
    </>
  )
}

function CategoriesCard({
  menu,
  context,
  counts,
}: {
  menu: Menu
  context: MenuContext
  counts: Map<string, MenuItem[]>
}) {
  const t = useT()
  const name = useLocalizedText(context.main)
  const patch = useMenuPatch(context.restaurantId)
  const [editing, setEditing] = useState<'new' | string | undefined>()
  const [status, setStatus] = useState<CardStatus>()

  // Sends the full order (F2); the response is the renumbered list, which replaces the cache.
  const reorder = useMutation({
    mutationFn: (ids: string[]) => {
      const body: ReorderMenuCategoriesRequest = { ids }
      return request<MenuCategoriesResponse>(menuPath(context.restaurantId, '/categories/order'), {
        method: 'PUT',
        body,
      })
    },
    onSuccess: (result) => patch((current) => ({ ...current, categories: result.items })),
  })

  function move(index: number, by: -1 | 1) {
    const ids = menu.categories.map((category) => category.id)
    const target = index + by
    ;[ids[index], ids[target]] = [ids[target]!, ids[index]!]
    setStatus(undefined)
    reorder.mutate(ids)
  }

  return (
    <Card className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">{t('menu.categoriesTitle')}</h2>
      <p className="text-muted text-sm">{t('menu.categoriesHint')}</p>
      <FormError error={reorder.error} />

      {menu.categories.length === 0 && editing !== 'new' && (
        <p className="text-muted text-sm">{t('menu.noCategories')}</p>
      )}
      {menu.categories.length > 0 && (
        <ol className="flex flex-col gap-2">
          {menu.categories.map((category, index) => {
            const label = name(category.name)
            const itemCount = counts.get(category.id)?.length ?? 0
            return (
              <li key={category.id} className="border-border flex flex-col gap-3 rounded-lg border p-3">
                {editing === category.id ? (
                  <CategoryForm
                    context={context}
                    current={category}
                    onSaved={() => {
                      setEditing(undefined)
                      setStatus('saved')
                    }}
                    onCancel={() => setEditing(undefined)}
                  />
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate font-medium">{label}</span>
                      <span className="text-muted text-sm">{t('menu.itemCount', { count: itemCount })}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={t('menu.moveUp', { name: label })}
                        disabled={index === 0 || reorder.isPending}
                        onClick={() => move(index, -1)}
                      >
                        ↑
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={t('menu.moveDown', { name: label })}
                        disabled={index === menu.categories.length - 1 || reorder.isPending}
                        onClick={() => move(index, 1)}
                      >
                        ↓
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        aria-label={`${t('menu.editCategory')}: ${label}`}
                        onClick={() => {
                          setStatus(undefined)
                          setEditing(category.id)
                        }}
                      >
                        {t('common.edit')}
                      </Button>
                      <ConfirmDeleteButton
                        label={label}
                        confirmLabel={t('menu.confirmDeleteCategoryOf', { name: label })}
                        refusal={itemCount > 0 ? t('error.CATEGORY_NOT_EMPTY', { items: itemCount }) : undefined}
                        remove={() =>
                          request<void>(menuPath(context.restaurantId, `/categories/${category.id}`), { method: 'DELETE' })
                        }
                        onDeleted={() => {
                          patch((current) => ({
                            ...current,
                            categories: current.categories.filter((other) => other.id !== category.id),
                          }))
                          setStatus('deleted')
                        }}
                      />
                    </span>
                  </div>
                )}
              </li>
            )
          })}
        </ol>
      )}

      <CardActions
        status={status}
        savedKey="menu.categorySaved"
        deletedKey="menu.categoryDeleted"
        addLabel={t('menu.addCategory')}
        onAdd={
          editing === undefined
            ? () => {
                setStatus(undefined)
                setEditing('new')
              }
            : undefined
        }
      />
      {editing === 'new' && (
        <div className="border-border border-t pt-4">
          <CategoryForm
            context={context}
            onSaved={() => {
              setEditing(undefined)
              setStatus('saved')
            }}
            onCancel={() => setEditing(undefined)}
          />
        </div>
      )}
    </Card>
  )
}

function CategoryForm({
  context,
  current,
  onSaved,
  onCancel,
}: {
  context: MenuContext
  current?: MenuCategory
  onSaved: () => void
  onCancel: () => void
}) {
  const t = useT()
  const patch = useMenuPatch(context.restaurantId)
  const [names, setNames] = useState<NameDraft>(() => namesFrom(current?.name))

  const save = useMutation({
    mutationFn: () => {
      const body: SaveMenuCategoryRequest = { name: toLocalizedText(names) }
      return current
        ? request<MenuCategory>(menuPath(context.restaurantId, `/categories/${current.id}`), { method: 'PUT', body })
        : request<MenuCategory>(menuPath(context.restaurantId, '/categories'), { method: 'POST', body })
    },
    onSuccess: (saved) => {
      patch((menu) => ({
        ...menu,
        categories: current
          ? menu.categories.map((category) => (category.id === saved.id ? saved : category))
          : [...menu.categories, saved],
      }))
      onSaved()
    },
  })

  function submit(event: SubmitEvent) {
    event.preventDefault()
    save.mutate()
  }

  const nameError = claim(save.error, 'MISSING_MAIN_LANGUAGE')
  return (
    <form onSubmit={submit} className="flex flex-col gap-4" aria-label={t(current ? 'menu.editCategory' : 'menu.addCategory')}>
      <LocalizedNameFields
        value={names}
        onChange={(next) => {
          if (save.error) save.reset()
          setNames(next)
        }}
        main={context.main}
        languages={context.languages}
        error={nameError}
        autoFocus
      />
      <FormError error={nameError ? undefined : save.error} />
      <div className="flex gap-2">
        <Button type="submit" loading={save.isPending}>
          {t('common.save')}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

// ---- Items ----

function ItemsCard({
  menu,
  context,
  category,
  items,
}: {
  menu: Menu
  context: MenuContext
  category: MenuCategory
  items: readonly MenuItem[]
}) {
  const t = useT()
  const language = useLanguage()
  const name = useLocalizedText(context.main)
  const patch = useMenuPatch(context.restaurantId)
  const [editing, setEditing] = useState<'new' | string | undefined>()
  const [status, setStatus] = useState<CardStatus>()
  const heading = name(category.name)

  return (
    <Card className="flex flex-col gap-4" aria-label={heading}>
      <h2 className="text-lg font-semibold">{heading}</h2>
      {items.length === 0 && editing !== 'new' && <p className="text-muted text-sm">{t('menu.noItems')}</p>}
      {items.length > 0 && (
        <ul className="flex flex-col gap-2">
          {items.map((item) => {
            const label = name(item.name)
            return (
              <li key={item.id} className="border-border flex flex-col gap-3 rounded-lg border p-3">
                {editing === item.id ? (
                  <ItemForm
                    menu={menu}
                    context={context}
                    current={item}
                    onSaved={() => {
                      setEditing(undefined)
                      setStatus('saved')
                    }}
                    onCancel={() => setEditing(undefined)}
                  />
                ) : (
                  <div className="flex items-center gap-3">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={t('menu.pictureOf', { name: label })}
                        className="size-16 shrink-0 rounded-lg object-cover"
                      />
                    ) : (
                      <span aria-hidden className="bg-accent-soft size-16 shrink-0 rounded-lg" />
                    )}
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-medium">{label}</span>
                      <span className="text-muted flex flex-wrap gap-x-3 text-sm">
                        <span>{formatPrice(item.price, context.decimals, language)}</span>
                        {item.step !== '1' && <span>{t(STEP_LABELS[item.step])}</span>}
                      </span>
                    </span>
                    <span className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="secondary"
                        aria-label={`${t('menu.editItem')}: ${label}`}
                        onClick={() => {
                          setStatus(undefined)
                          setEditing(item.id)
                        }}
                      >
                        {t('common.edit')}
                      </Button>
                      <ConfirmDeleteButton
                        label={label}
                        confirmLabel={t('menu.confirmDeleteItemOf', { name: label })}
                        remove={() => request<void>(menuPath(context.restaurantId, `/items/${item.id}`), { method: 'DELETE' })}
                        onDeleted={() => {
                          patch((current) => ({ ...current, items: current.items.filter((other) => other.id !== item.id) }))
                          setStatus('deleted')
                        }}
                      />
                    </span>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      <CardActions
        status={status}
        savedKey="menu.itemSaved"
        deletedKey="menu.itemDeleted"
        addLabel={t('menu.addItem')}
        addAriaLabel={`${t('menu.addItem')}: ${heading}`}
        onAdd={
          editing === undefined
            ? () => {
                setStatus(undefined)
                setEditing('new')
              }
            : undefined
        }
      />
      {editing === 'new' && (
        <div className="border-border border-t pt-4">
          <ItemForm
            menu={menu}
            context={context}
            categoryId={category.id}
            onSaved={() => {
              setEditing(undefined)
              setStatus('saved')
            }}
            onCancel={() => setEditing(undefined)}
          />
        </div>
      )}
    </Card>
  )
}

interface ItemDraft {
  names: NameDraft
  categoryId: string
  price: string
  step: QuantityStep
  /** What the item will picture: the saved one, a fresh upload, or nothing. */
  picture: Pick<Image, 'id' | 'url'> | null
}

/** A form-level failure the price field explains better than the banner. */
function priceHint(t: Translate, menu: Menu, decimals: number): string {
  const currencies = menu.currencies.length > 0 ? `${t('menu.priceIn', { currencies: menu.currencies.join(', ') })} ` : ''
  return currencies + (decimals === 0 ? t('menu.priceWhole') : t('menu.priceDecimals', { decimals }))
}

/**
 * Create or full-replace one item. Pictures upload as chosen (F1); an upload
 * discarded before saving is deleted here, a replaced saved one by the backend (F2).
 */
function ItemForm({
  menu,
  context,
  current,
  categoryId,
  onSaved,
  onCancel,
}: {
  menu: Menu
  context: MenuContext
  current?: MenuItem
  categoryId?: string
  onSaved: () => void
  onCancel: () => void
}) {
  const t = useT()
  const patch = useMenuPatch(context.restaurantId)
  const [draft, setDraft] = useState<ItemDraft>(() => ({
    names: namesFrom(current?.name),
    categoryId: current?.category_id ?? categoryId ?? menu.categories[0]?.id ?? '',
    price: current?.price ?? '',
    step: current?.step ?? '1',
    picture: current?.image_id && current.image_url ? { id: current.image_id, url: current.image_url } : null,
  }))
  // The upload of this form session that no saved item references yet.
  const pendingId = draft.picture && draft.picture.id !== current?.image_id ? draft.picture.id : undefined
  const name = useLocalizedText(context.main)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const save = useMutation({
    mutationFn: () => {
      const body: SaveMenuItemRequest = {
        name: toLocalizedText(draft.names),
        category_id: draft.categoryId,
        price: draft.price.trim(),
        step: draft.step,
        image_id: draft.picture?.id ?? null,
      }
      return current
        ? request<MenuItem>(menuPath(context.restaurantId, `/items/${current.id}`), { method: 'PUT', body })
        : request<MenuItem>(menuPath(context.restaurantId, '/items'), { method: 'POST', body })
    },
    onSuccess: (saved) => {
      patch((cached) => ({
        ...cached,
        items: current ? cached.items.map((item) => (item.id === saved.id ? saved : item)) : [...cached.items, saved],
      }))
      onSaved()
    },
    onError: (error) => {
      // The upload vanished under us (F2): nothing to keep, the user re-picks.
      if (claim(error, 'IMAGE_NOT_FOUND')) setDraft((d) => ({ ...d, picture: null }))
    },
  })

  const discard = (id: string) => void request<void>(imagesPath(context.restaurantId, `/${id}`), { method: 'DELETE' }).catch(() => {})

  const pick = useMutation({
    mutationFn: async (file: File) => upload<Image>(imagesPath(context.restaurantId), await prepareImageUpload(file)),
    onSuccess: (image) => {
      // The form went away mid-upload: nothing will ever save this one.
      if (!mounted.current) return discard(image.id)
      if (pendingId) discard(pendingId)
      setDraft((d) => ({ ...d, picture: { id: image.id, url: image.url } }))
    },
  })

  function choose(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (save.error) save.reset()
    pick.mutate(file)
  }

  function removePicture() {
    if (pendingId) discard(pendingId)
    setDraft((d) => ({ ...d, picture: null }))
  }

  function cancel() {
    if (pendingId) discard(pendingId)
    onCancel()
  }

  function submit(event: SubmitEvent) {
    event.preventDefault()
    save.mutate()
  }

  const edit = (changes: Partial<ItemDraft>) => {
    if (save.error) save.reset()
    setDraft((d) => ({ ...d, ...changes }))
  }

  const nameError = claim(save.error, 'MISSING_MAIN_LANGUAGE')
  const priceError = claim(save.error, 'INVALID_PRICE')
  const pictureError = claim(save.error, 'IMAGE_NOT_FOUND') ?? pick.error
  const bannerError = nameError || priceError || claim(save.error, 'IMAGE_NOT_FOUND') ? undefined : save.error
  const label = current ? name(current.name) : undefined

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" aria-label={t(current ? 'menu.editItem' : 'menu.addItem')}>
      <LocalizedNameFields
        value={draft.names}
        onChange={(names) => edit({ names })}
        main={context.main}
        languages={context.languages}
        error={nameError}
        autoFocus
      />
      <Field label={t('menu.category')}>
        <Select value={draft.categoryId} onChange={(e) => edit({ categoryId: e.target.value })}>
          {menu.categories.map((category) => (
            <option key={category.id} value={category.id}>
              {name(category.name)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('menu.price')} hint={priceHint(t, menu, context.decimals)} error={priceError}>
        <Input
          type="number"
          inputMode="decimal"
          required
          min="0"
          step={priceStep(context.decimals)}
          value={draft.price}
          onChange={(e) => edit({ price: e.target.value })}
        />
      </Field>
      <Field label={t('menu.step')} hint={t('menu.stepHint')}>
        <Select value={draft.step} onChange={(e) => edit({ step: e.target.value as QuantityStep })}>
          {STEPS.map((step) => (
            <option key={step} value={step}>
              {t(STEP_LABELS[step])}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('menu.picture')} hint={t('menu.pictureHint', { max_mb: MAX_IMAGE_MB })} error={pictureError}>
        <div className="flex flex-wrap items-center gap-3">
          {draft.picture && (
            <img
              src={draft.picture.url}
              alt={label ? t('menu.pictureOf', { name: label }) : t('menu.picture')}
              className="size-24 shrink-0 rounded-lg object-cover"
            />
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Input type="file" accept="image/*" disabled={pick.isPending} onChange={choose} className="py-2" />
            {pick.isPending && (
              <p role="status" className="text-muted text-sm">
                {t('menu.uploading')}
              </p>
            )}
            {draft.picture && !pick.isPending && (
              <Button size="sm" variant="ghost" className="self-start" onClick={removePicture}>
                {t('menu.removePicture')}
              </Button>
            )}
          </div>
        </div>
      </Field>
      <FormError error={bannerError} />
      <div className="flex gap-2">
        <Button type="submit" loading={save.isPending} disabled={pick.isPending}>
          {t('common.save')}
        </Button>
        <Button type="button" variant="ghost" disabled={pick.isPending} onClick={cancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}
